import i18n from '@/i18n'
import { INetwork } from '@/types'
import { isMobileDevice, SUPPORTED_NETWORK, WALLET_UNISAT } from '@/utils'
import { CustomError } from '@/utils/error'
import { sendLog } from '@/utils/logger'

import { BaseWallet } from './BaseWallet'
import { EventEmitter, IWalletConnectOptions, UnisatEventMap, UnisatProvider, UnisatWalletOptions } from './types'

function getProvider(): UnisatProvider | undefined {
  return window.unisat_wallet
}

export class UnisatWallet extends EventEmitter<UnisatEventMap> implements BaseWallet {
  name = 'UniSat'
  key = WALLET_UNISAT
  private provider: UnisatProvider | null = null
  private accountsChangedListener: ((accounts: string[]) => void) | null = null
  private networkChangedListener: ((network: string) => void) | null = null
  private currentNetwork: string | null = null
  private connectedAddresses: Record<string, string> = {}

  constructor() {
    super()
    this.initialize()
  }

  isDetected(): boolean {
    return !!getProvider()
  }

  private download(): void {
    if (isMobileDevice()) throw new CustomError(i18n.t('wallet_error.UNISAT_MOBILE_ERROR'))
    throw new CustomError(
      i18n.t('wallet_error.NOT_INSTALLED_ERROR', { wallet: this.name, url: 'https://unisat.io/download' })
    )
  }

  private getWalletProvider(): UnisatProvider | null {
    const provider = getProvider()
    if (provider) {
      return provider
    }
    return null
  }

  private handleAccountsChanged = (accounts: string[]): void => {
    this.emit('accountsChanged', accounts)
  }

  private handleNetworkChanged = (network: string): void => {
    this.emit('networkChanged', network)
  }

  initialize(): void {
    if (!this.provider) {
      this.provider = this.getWalletProvider()

      if (this.provider) {
        this.provider.on('accountsChanged', this.handleAccountsChanged)
        this.provider.on('networkChanged', this.handleNetworkChanged)
      }
    }
  }

  isInstalled(): boolean {
    return typeof window.unisat_wallet !== 'undefined'
  }

  private setupEventListeners({ onAccountChanged, network, onDisconnected }: IWalletConnectOptions): void {
    if (!this.provider) return

    if (this.accountsChangedListener) {
      this.provider.removeListener('accountsChanged', this.accountsChangedListener)
      this.accountsChangedListener = null
    }

    if (this.networkChangedListener) {
      this.provider.removeListener('networkChanged', this.networkChangedListener)
      this.networkChangedListener = null
    }

    this.accountsChangedListener = (accounts: string[]) => {
      if (accounts && accounts.length > 0) {
        this.connectedAddresses[network.networkId] = accounts[0]
        onAccountChanged?.({ address: accounts[0], networkInfo: network })
      } else if (onDisconnected) {
        onDisconnected(this.key)
      }
    }

    this.networkChangedListener = (networkType: string) => {
      const chooseNetwork = this.currentNetwork === SUPPORTED_NETWORK.BTC_TESTNET ? 'testnet' : 'livenet'
      if (networkType !== chooseNetwork && onDisconnected) {
        onDisconnected(this.key)
      }
    }

    this.provider.on('accountsChanged', this.accountsChangedListener)
    this.provider.on('networkChanged', this.networkChangedListener)
  }

  async connect(
    networkId: string,
    options: IWalletConnectOptions
  ): Promise<{ address: string; networkInfo: INetwork; compressedPublicKey: string | undefined }> {
    if (!this.isInstalled()) {
      this.download()
    }

    if (!this.provider) {
      this.initialize()
      if (!this.provider) {
        throw new Error('Provider not initialized')
      }
    }

    this.currentNetwork = networkId

    this.setupEventListeners(options)

    const accounts = await this.provider.requestAccounts()
    if (!accounts || accounts.length === 0) {
      throw new Error('No accounts found')
    }

    const currentNetwork = await this.provider.getNetwork()
    const chooseNetwork = networkId === SUPPORTED_NETWORK.BTC_TESTNET ? 'testnet' : 'livenet'

    if (currentNetwork !== chooseNetwork) {
      await this.provider.switchNetwork(chooseNetwork)
    }

    const publicKey = await this.provider.getPublicKey()
    this.connectedAddresses[networkId] = accounts[0]

    return {
      address: accounts[0],
      networkInfo: options.network,
      compressedPublicKey: publicKey,
    }
  }

  async disconnect(networkId?: string): Promise<void> {
    if (!networkId) throw new Error(`Cannot disconnect network ${networkId}`)
    if (this.provider) {
      if (this.accountsChangedListener) {
        this.provider.removeListener('accountsChanged', this.accountsChangedListener)
        this.accountsChangedListener = null
      }

      if (this.networkChangedListener) {
        this.provider.removeListener('networkChanged', this.networkChangedListener)
        this.networkChangedListener = null
      }

      this.currentNetwork = null
      this.provider = null
      delete this.connectedAddresses[networkId]
    }
  }

  async getAddress(networkId: string): Promise<string> {
    if (!this.isInstalled() || !this.provider) {
      throw new CustomError('UniSat Wallet is not installed')
    }
    const accounts = await this.provider.getAccounts()
    this.connectedAddresses[networkId] = accounts[0]
    return accounts[0]
  }

  async sendTransaction(
    toAddress: string,
    amount: number | bigint,
    networkId: string,
    options?: UnisatWalletOptions
  ): Promise<string> {
    const accounts = await this.provider?.requestAccounts()
    sendLog({
      message: 'unisat sendTransaction',
      data: { network: networkId, toAddress, options, amount, accounts },
      tags: this.key,
    })
    if (!this.isInstalled() || !this.provider) {
      throw new CustomError('UniSat Wallet is not installed')
    }

    try {
      return await this.provider.sendBitcoin(toAddress, Number(amount), {
        feeRate: options?.feeRate,
      })
    } catch (error) {
      throw new Error(`Failed to send transaction: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  async signMessage(message: string, network?: string): Promise<string> {
    const accounts = await this.provider?.requestAccounts()
    sendLog({
      message: 'unitsat signMessage accounts',
      data: accounts,
      tags: this.key,
    })
    if (!this.isInstalled() || !this.provider) {
      throw new CustomError('UniSat Wallet is not installed')
    }

    if (!network) {
      throw new Error('NetworkId is not found')
    }

    try {
      return await this.provider.signMessage(message)
    } catch (error) {
      throw new Error(`Failed to sign message: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  async getCompressedPublicKey(network?: string): Promise<string> {
    if (!this.isInstalled() || !this.provider) {
      throw new CustomError('UniSat Wallet is not installed')
    }

    if (!network) {
      throw new Error('NetworkId is not found')
    }

    try {
      return await this.provider.getPublicKey()
    } catch (error) {
      throw new Error(`Failed to get public key: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  async signPsbt(
    psbt: string,
    network: string,
    options?: {
      autoFinalized?: boolean
      toSignInputs?: Array<{
        index: number
        address?: string
        publicKey?: string
        sighashTypes?: number[]
      }>
    }
  ): Promise<string> {
    sendLog({
      message: 'unitsat signPsbt',
      data: { network, psbt, options },
      tags: this.key,
    })
    if (!this.isInstalled() || !this.provider) {
      throw new CustomError('UniSat Wallet is not installed')
    }

    try {
      return await this.provider.signPsbt(psbt, options)
    } catch (error) {
      throw new Error(`Failed to sign PSBT: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  priority(): number {
    return this.isInstalled() ? 0 : 1
  }
}
